import { Language } from '../'
import { logger } from '../util/Logger'
import { Board } from './Board'
import { Player } from './Player'

export enum ActionHint {
  BLANK = 'BLANK',
  FOLD = 'FOLD',
  CHECK = 'CHECK',
  BET = 'BET',
  RAISE = 'RAISE',
  CALL = 'CALL',
  ALLIN = 'ALLIN'
}

export class ActionEvaluator {
  /**
   * Retrieves action hints, which are available actions given the state
   * of the board and player.
   *
   * If there are bets,
   *   -> a player may call (match the bet)
   *   -> a player may raise (increase the biggest bet)
   *   -> a player may fold (reduce their losses and get out)
   * If there are no bets,
   *   -> a player may check (indicate they do not want to bet, retain their options
   *   -> a player may bet (set the first bet in a show of trust in their cards or bravado?)
   */
  public static getActionHints(board: Board, player: Player, bigBlindPlayer: Player): ActionHint[] {
    const playerName = player.name
    logger.trace(`[ActionEvaluator.getActionHints] playerName ${playerName}`)
    logger.trace(`[ActionEvaluator.getActionHints] board.isPreDeal ${board.isPreDeal()}`)
    logger.trace(`[ActionEvaluator.getActionHints] board.isShowdown ${board.isShowdown()}`)
    if (board.isPreDeal() || board.isShowdown()) {
      return []
    }

    //Player bet
    logger.trace(`[ActionEvaluator.getActionHints] player.bets ${player.bets.length}`)
    const playerBet = player.bets.reduce((overallBet, bet) => {
      overallBet += bet.amount
      return overallBet
    }, 0)

    logger.trace(`[ActionEvaluator.getActionHints] playerBet ${playerBet}`)

    //Betting conditions
    const boardHasBets = board.hasBetsNoChecks()
    //if the small blind calls, the BB should be shown raise/check rather than bet/check
    const bigBlindCalled =
      bigBlindPlayer &&
      player.getId() === bigBlindPlayer.getId() &&
      board.biggestBet() === board.blinds.big &&
      playerBet === board.blinds.big

    logger.trace(`[ActionEvaluator.getActionHints] bigBlindCalled ${bigBlindCalled}`)

    logger.trace(`[ActionEvaluator.getActionHints] player.chips ${player.chips}`)
    logger.trace(`[ActionEvaluator.getActionHints] board.biggestBet() ${board.biggestBet()}`)

    //players can only raise in increments of the biggestBet
    const playerCanRaise = player.chips / board.biggestBet() > 1
    logger.trace(`[ActionEvaluator.getActionHints] ${playerName} playerCanRaise ${playerCanRaise}`)

    //players can call if their chips and their bet meet the board's biggest bet
    const playerCanCall = player.chips + playerBet - board.biggestBet() > 0
    logger.trace(`[ActionEvaluator.getActionHints] ${playerName} playerCanCall ${playerCanCall}`)

    const hints: ActionHint[] = []

    if (bigBlindCalled) {
      if (playerCanRaise) {
        hints.push(ActionHint.RAISE)
      }
      hints.push(ActionHint.CHECK)
    } else if (boardHasBets) {
      if (!playerCanCall) {
        hints.push(ActionHint.ALLIN)
      } else {
        if (playerCanRaise) {
          hints.push(ActionHint.RAISE)
        } else if (player.chips > 0) {
          hints.push(ActionHint.ALLIN)
        }
        hints.push(ActionHint.CALL)
      }

      hints.push(ActionHint.FOLD)
    } else {
      if (!playerCanCall) {
        hints.push(ActionHint.ALLIN)
      } else {
        hints.push(ActionHint.BET)
      }

      hints.push(ActionHint.CHECK)
    }

    if (hints.length === 0) {
      throw Error(Language.strings.error.actions.noActionHintsError)
    }

    logger.trace(`[ActionEvaluator.getActionHints] hints ${hints.length} ${JSON.stringify(hints)}`)

    return hints
  }
}
